import { useState, useMemo, useEffect } from "react";
import { Button, Grid2 as Grid, Typography } from "@mui/material";
import { FormAutocomplete, FormSelect, FormTextField } from "../Form";
import { State, City } from "country-state-city";
import { useUser } from "../../../hooks/useUser";

const userTypeOptions = [
    "Advertiser - Increase reach of my product to players.",
    // "Coach - Manage and connect with players.",
    // "Scout - Find your next baseball talent."
]

type RegisterFormType = {
    handleSignup: (v: { [key: string]: any }) => void;
    isLoading: boolean;
    setIsLoading: (v: boolean) => void;
    autoFillColor: string;
}

const RegisterForm = ({ handleSignup, isLoading, setIsLoading, autoFillColor }: RegisterFormType) => {
    // User Type input
    const user = useUser();
    const [userType, setUserType] = useState("");

    // Account Information input
    const [businessName, setBusinessName] = useState(user?.businessName ?? "");
    const [email, setEmail] = useState(user?.email ?? "");
    const [mobile, setMobile] = useState(user?.mobile ?? "");
    const [addressLine1, setAddressLine1] = useState(user?.addressLine1 ?? "");
    const [addressLine2, setAddressLine2] = useState(user?.addressLine2 ?? "");
    const country = { label: "Canada", value: "CA" };
    const [state, setState] = useState<{ label: string, value: string} | null>(null);
    const [city, setCity] = useState<{ label: string, value: string} | null>(null);
    const [postalCode, setPostalCode] = useState(user?.postalCode ?? "");
    const [password, setPassword] = useState("");
    const [password2, setPassword2] = useState("");

    const [businessNameError, setBusinessNameError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [mobileError, setMobileError] = useState("");
    const [postalCodeError, setPostalCodeError] = useState("");
    const [passwordError, setPasswordError] = useState("");

    useEffect(() => {
        if (user) {
            if (user.province) {
                const stateCode = State.getStatesOfCountry("CA").find((item) => item.name === user.province)?.isoCode;
                if (stateCode !== undefined) {
                    setState({ label: user.province, value: stateCode });
                }
            }
            if (user.city) {
                setCity({ label: user.city, value: user.city });
            }
        }
    }, [user]);

    const handleStateChange = (v: { label: string, value: string} | null) => {
        setState(v);
        setCity(null);
    }

    const getStates = useMemo(() => {
        return State.getStatesOfCountry("CA").map((state) => {
            return { label: state.name, value: state.isoCode };
        });
    }, []);

    const getCities = useMemo(() => {
        if (state !== null) {
            return City.getCitiesOfState("CA", state?.value).map((city) => {
                return { label: city.name, value: city.name };
            });
        }
        return [];
    }, [state]);

    function validateAccountInfo() {
        var valid = true;
        if (businessName.length < 4 || businessName.length > 40) {
            setBusinessNameError("Business name must be between 4-40 characters");
            valid = false;
        }
        if (!email.match(/^[\S]+@([\w-]+\.)+[\w-]{2,4}$/g)) {
            setEmailError("Invalid email");
            valid = false;
        }
        if (mobile.length > 0 && !mobile.match('[0-9]{10}')) {
            setMobileError("Mobile should be 10 digits long")
            valid = false;
        }
        if (!postalCode.match(/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/)) {
            setPostalCodeError("Invalid postal code");
            valid = false;
        }
        if (user === null && password.length < 6) {
            setPasswordError("Password should be at least 6 characters long")
            valid = false;
        } else if (password !== password2) {
            setPasswordError("Passwords should match")
            valid = false;
        }
        return valid
    }

    useEffect(() => {
        setBusinessNameError("");
    }, [businessName]);

    useEffect(() => {
        setEmailError("");
    }, [email]);

    useEffect(() => {
        setMobileError("");
    }, [mobile]);

    useEffect(() => {
        setPostalCodeError("");
    }, [postalCode]);

    useEffect(() => {
        setPasswordError("");
    }, [password, password2]);

    const onSubmit = (event: any) => {
        event.preventDefault();
        setIsLoading(true);
        if (validateAccountInfo()) {
            handleSignup(
                {
                    id: user?.id,
                    businessName,
                    email,
                    password,
                    userType,
                    mobile,
                    addressLine1,
                    addressLine2,
                    country: country?.value,
                    state: state?.label,
                    city: city?.value,
                    postalCode,
                }
            )
        }
        setIsLoading(false);
    }

    return (
        <Grid container spacing={3} width={"100%"} component={"form"} onSubmit={onSubmit} sx={{
            input: {
                '&:-webkit-autofill': {
                    WebkitBoxShadow: `0 0 0 30px ${autoFillColor} inset !important;`
                }
            }
        }}>
            {user === null &&
                <FormSelect
                    id="userType"
                    label="What type of user are you?"
                    value={userType}
                    setValue={setUserType}
                    items={userTypeOptions}
                    itemValues={[1, 2, 3]}
                    required={true}
                    resettable={false}
                    size={12}
                />
            }
            <Typography variant="h6">Account Info</Typography>
            <FormTextField
                label="Business Name"
                value={businessName}
                setValue={setBusinessName}
                error={businessNameError}
                required
                size={12}
            />
            <FormTextField
                label="Email"
                value={email}
                setValue={setEmail}
                error={emailError}
                required
                type="email"
            />
            <FormTextField
                label="Mobile"
                value={mobile}
                setValue={setMobile}
                error={mobileError}
                type="tel"
            />
            <Grid size={12}><Typography variant="h6">Address</Typography></Grid>
            <FormTextField
                label="Address Line 1"
                value={addressLine1}
                setValue={setAddressLine1}
                required
                size={12}
            />
            <FormTextField
                label="Address Line 2"
                value={addressLine2}
                setValue={setAddressLine2}
                size={8}
            />
            <FormTextField
                label="Postal Code"
                value={postalCode}
                setValue={setPostalCode}
                error={postalCodeError}
                required
                size={4}
            />
            <FormAutocomplete
                label="Country"
                value={country}
                setValue={() => {}}
                items={[]}
                disabled={true}
                required
            />
            <FormAutocomplete
                label="Province"
                value={state}
                setValue={handleStateChange}
                items={getStates}
                required
            />
            <FormAutocomplete
                label="City"
                value={city}
                setValue={setCity}
                items={getCities}
                disabled={state === null}
                required
            />
            {user === null ? (
                <Grid size={12}><Typography variant="h6">Password</Typography></Grid>
            ) : (
                <Grid size={12}>
                    <Typography variant="h5">Change Password</Typography>
                    <Typography color="textSecondary">(Leave blank if not updating)</Typography>
                </Grid>
            )}
            <FormTextField
                label="Password"
                value={password}
                setValue={setPassword}
                error={passwordError}
                required={user === null}
                type="password"
            />
            <FormTextField
                label="Confirm Password"
                value={password2}
                setValue={setPassword2}
                required={user === null}
                type="password"
            />
            <Button
                variant="contained"
                size="large"
                disabled={
                    businessNameError.length > 0 ||
                    emailError.length > 0 ||
                    mobileError.length > 0 ||
                    passwordError.length > 0 ||
                    isLoading
                }
                fullWidth
                type="submit"
            >
                {user === null ? "Register" : "Save Profile"}
            </Button>
        </Grid>
    )
}

export default RegisterForm;
